import React, { useState } from 'react';
import { CalendarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

interface DateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  className?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme } = useTheme();

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleQuickSelect = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    onStartDateChange(start);
    onEndDateChange(end);
    setIsOpen(false);
  };

  const handleClear = () => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    onStartDateChange(weekAgo);
    onEndDateChange(today);
  };

  const quickOptions = [
    { label: '7 дней', days: 7 },
    { label: '30 дней', days: 30 },
    { label: '90 дней', days: 90 },
    { label: 'Год', days: 365 }
  ];

  return (
    <div className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-3 text-sm border border-input bg-background text-foreground rounded-lg transition-colors hover:bg-accent hover:border-border"
      >
        <div className="flex items-center space-x-2">
          <CalendarIcon className="h-5 w-5" />
          <span className="hidden sm:inline">Период:</span>
          <span className="font-medium">
            {formatDate(startDate)} - {formatDate(endDate)}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
            title="Сбросить к последним 7 дням"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-lg shadow-lg border border-border bg-popover">
          <div className="p-4">
            {/* Quick options */}
            <div className="mb-4">
              <h3 className="text-sm font-medium mb-3 text-popover-foreground">
                Быстрый выбор
              </h3>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {quickOptions.map((option) => (
                  <button
                    key={option.days}
                    type="button"
                    onClick={() => handleQuickSelect(option.days)}
                    className="px-3 py-2 text-sm font-medium rounded-md transition-colors bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom date inputs */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-popover-foreground">
                Произвольный период
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-2 text-popover-foreground/70">
                    Начальная дата
                  </label>
                  <input
                    type="date"
                    value={startDate.toISOString().split('T')[0]}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      if (newDate <= endDate) {
                        onStartDateChange(newDate);
                      }
                    }}
                    max={endDate.toISOString().split('T')[0]}
                    className="w-full px-3 py-2 text-sm border border-input bg-background text-foreground rounded-md transition-colors focus:border-ring focus:ring-ring focus-visible:outline-none focus-visible:ring-2"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium mb-2 text-popover-foreground/70">
                    Конечная дата
                  </label>
                  <input
                    type="date"
                    value={endDate.toISOString().split('T')[0]}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      if (newDate >= startDate) {
                        onEndDateChange(newDate);
                      }
                    }}
                    min={startDate.toISOString().split('T')[0]}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 text-sm border border-input bg-background text-foreground rounded-md transition-colors focus:border-ring focus:ring-ring focus-visible:outline-none focus-visible:ring-2"
                  />
                </div>
              </div>
            </div>

            {/* Close button */}
            <div className="mt-4 pt-3 border-t border-border">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="w-full px-4 py-2 text-sm font-medium rounded-md transition-colors bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Применить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default DateRangePicker;
